/**
 * Global Input Protection Service
 * Automatically sanitizes all text inputs across the application
 */

import { sanitizeInput, containsDangerousUnicode } from '../utils/inputSanitizer';
import { defaultSanitizationConfig } from '../config/sanitizationConfig';

class GlobalInputProtection {
  constructor(config = {}) {
    this.config = { ...defaultSanitizationConfig, ...config };
    this.initialized = false;
    this.notificationTimeout = null;
    this.notificationElement = null;
    this.lastNotificationTime = 0;
    
    // Bind methods to preserve context
    this.handleInput = this.handleInput.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
  }

  /**
   * Initialize the global protection system
   */
  init() {
    if (this.initialized || !this.config.enabled) {
      return;
    }

    // Add event listeners in capture phase (before React processes them)
    document.addEventListener('input', this.handleInput, true);
    document.addEventListener('paste', this.handlePaste, true);

    this.initialized = true;

    if (this.config.devWarnings) {
      console.log('[GlobalInputProtection] Initialized - All inputs are now protected');
    }
  }

  /**
   * Clean up and remove event listeners
   */
  destroy() {
    if (!this.initialized) {
      return;
    }

    document.removeEventListener('input', this.handleInput, true);
    document.removeEventListener('paste', this.handlePaste, true);

    this.clearNotification();
    this.initialized = false;

    if (this.config.devWarnings) {
      console.log('[GlobalInputProtection] Destroyed');
    }
  }

  /**
   * Check if an element should be excluded from sanitization
   */
  shouldExclude(element) {
    if (!element) return true;

    // Check against exclude selectors
    for (const selector of this.config.excludeSelectors) {
      try {
        if (element.matches(selector)) {
          return true;
        }
      } catch (e) {
        // Invalid selector, skip
      }
    }

    // Check if element has disabled attribute
    if (element.disabled || element.readOnly) {
      return true;
    }

    return false;
  }

  /**
   * Determine if newlines should be preserved for this element
   */
  shouldPreserveNewlines(element) {
    const tagName = element.tagName;
    return this.config.preserveNewlinesByElement[tagName] ?? true;
  }

  /**
   * Update input value and sync with React
   */
  updateInputValue(element, newValue) {
    const currentValue = element.value;
    
    if (currentValue === newValue) {
      return false; // No change needed
    }

    // Store cursor position
    const start = element.selectionStart;
    const end = element.selectionEnd;

    // Update the native input value
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      element.constructor.prototype,
      'value'
    )?.set;

    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(element, newValue);
    } else {
      element.value = newValue;
    }

    // Restore cursor position (adjust for removed characters)
    const diff = currentValue.length - newValue.length;
    const newStart = Math.max(0, start - diff);
    const newEnd = Math.max(0, end - diff);

    try {
      if (element.setSelectionRange && typeof newStart === 'number') {
        element.setSelectionRange(newStart, newEnd);
      }
    } catch (e) {
      // Some input types don't support selection range
    }

    // Dispatch input event to trigger React's onChange
    const event = new Event('input', { bubbles: true });
    element.dispatchEvent(event);

    return true; // Value was changed
  }

  /**
   * Handle input events (typing)
   */
  handleInput(event) {
    const element = event.target;

    // Only process text inputs
    if (!this.isTextInput(element)) {
      return;
    }

    // Check if should exclude
    if (this.shouldExclude(element)) {
      return;
    }

    const originalValue = element.value;

    // Quick check - if no dangerous characters, skip
    if (!containsDangerousUnicode(originalValue)) {
      return;
    }

    // Sanitize the input
    const sanitizedValue = sanitizeInput(originalValue, {
      preserveNewlines: this.shouldPreserveNewlines(element),
      preserveTabs: this.config.preserveTabs,
    });

    // If value changed, update it
    if (originalValue !== sanitizedValue) {
      this.updateInputValue(element, sanitizedValue);
      this.handleSanitizationOccurred(originalValue, sanitizedValue, element);
    }
  }

  /**
   * Handle paste events
   */
  handlePaste(event) {
    const element = event.target;

    // Only process text inputs
    if (!this.isTextInput(element)) {
      return;
    }

    // Check if should exclude
    if (this.shouldExclude(element)) {
      return;
    }

    // Get pasted data
    const pastedData = event.clipboardData?.getData('text');
    
    if (!pastedData) {
      return;
    }

    // Quick check - if no dangerous characters, allow default behavior
    if (!containsDangerousUnicode(pastedData)) {
      return;
    }

    // Sanitize the pasted content
    const sanitizedData = sanitizeInput(pastedData, {
      preserveNewlines: this.shouldPreserveNewlines(element),
      preserveTabs: this.config.preserveTabs,
    });

    // If data was modified, prevent default and insert sanitized version
    if (pastedData !== sanitizedData) {
      event.preventDefault();

      // Get current value and cursor position
      const currentValue = element.value || '';
      const start = element.selectionStart || 0;
      const end = element.selectionEnd || 0;

      // Insert sanitized data at cursor position
      const newValue = currentValue.substring(0, start) + sanitizedData + currentValue.substring(end);

      // Update the input
      this.updateInputValue(element, newValue);

      // Set cursor position after pasted content
      const newPosition = start + sanitizedData.length;
      try {
        if (element.setSelectionRange) {
          element.setSelectionRange(newPosition, newPosition);
        }
      } catch (e) {
        // Some input types don't support selection range
      }

      this.handleSanitizationOccurred(pastedData, sanitizedData, element);
    }
  }

  /**
   * Check if element is a text input
   */
  isTextInput(element) {
    if (!element) return false;

    const tagName = element.tagName;
    
    // Textarea
    if (tagName === 'TEXTAREA') {
      return true;
    }

    // Input elements
    if (tagName === 'INPUT') {
      const type = element.type?.toLowerCase() || 'text';
      // Only text-based input types
      return ['text', 'email', 'search', 'tel', 'url', 'password'].includes(type);
    }

    // Contenteditable elements
    if (element.contentEditable === 'true') {
      return true;
    }

    return false;
  }

  /**
   * Handle when sanitization occurs
   */
  handleSanitizationOccurred(original, sanitized, element) {
    // Call custom callback if provided
    if (typeof this.config.onSanitize === 'function') {
      this.config.onSanitize(original, sanitized, element);
    }

    // Call dangerous input callback if provided
    if (typeof this.config.onDangerousInput === 'function') {
      this.config.onDangerousInput(original, element);
    }

    // Show notification if enabled
    if (this.config.showNotifications) {
      this.showNotification();
    }

    // Log in development
    if (this.config.devWarnings) {
      console.warn('[GlobalInputProtection] Sanitized input:', {
        original: original.substring(0, 100),
        sanitized: sanitized.substring(0, 100),
        element: element.tagName,
        removed: original.length - sanitized.length,
      });
    }
  }

  /**
   * Show notification to user
   */
  showNotification() {
    // Throttle notifications (max one per second)
    const now = Date.now();
    if (now - this.lastNotificationTime < 1000) {
      return;
    }
    this.lastNotificationTime = now;

    // Clear existing notification
    this.clearNotification();

    // Create notification element
    this.notificationElement = document.createElement('div');
    this.notificationElement.textContent = this.config.notificationMessage;
    
    // Apply styles
    Object.assign(this.notificationElement.style, this.config.notificationStyle);

    // Add CSS animation
    this.addNotificationAnimation();

    // Add to document
    document.body.appendChild(this.notificationElement);

    // Auto-remove after duration
    this.notificationTimeout = setTimeout(() => {
      this.clearNotification();
    }, this.config.notificationDuration);
  }

  /**
   * Clear notification
   */
  clearNotification() {
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
      this.notificationTimeout = null;
    }

    if (this.notificationElement && this.notificationElement.parentNode) {
      this.notificationElement.parentNode.removeChild(this.notificationElement);
      this.notificationElement = null;
    }
  }

  /**
   * Add CSS animation for notification
   */
  addNotificationAnimation() {
    // Check if animation is already added
    if (document.getElementById('global-input-protection-animation')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'global-input-protection-animation';
    style.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }
}

// Singleton instance
let instance = null;

/**
 * Initialize global input protection
 * @param {Object} config - Configuration options
 * @returns {GlobalInputProtection} - The protection instance
 */
export const initGlobalInputProtection = (config = {}) => {
  if (!instance) {
    instance = new GlobalInputProtection(config);
  } else {
    instance.updateConfig(config);
  }
  
  instance.init();
  return instance;
};

/**
 * Get the current instance
 * @returns {GlobalInputProtection|null}
 */
export const getGlobalInputProtection = () => {
  return instance;
};

/**
 * Destroy global input protection
 */
export const destroyGlobalInputProtection = () => {
  if (instance) {
    instance.destroy();
    instance = null;
  }
};

export default GlobalInputProtection;

